/**
 * Cache-busting for social preview images.
 *
 * Facebook, LinkedIn and X cache the og:image they scraped the first time a
 * URL was shared and only re-fetch when the *image URL itself* changes. After
 * re-generating / re-compressing our social artwork, the old (sometimes
 * 404ing) preview would otherwise stick around indefinitely.
 *
 * Every og:image / twitter:image therefore carries a `?v=` stamp. Bump
 * OG_IMAGE_VERSION whenever the shared artwork changes and crawlers treat it
 * as a brand new image. Content-owned images (articles, events) additionally
 * fold in their own updated_at so editing a post refreshes its card without a
 * global bump.
 */

// Bump this when public/images/og-image.jpg (or any sitewide social art)
// is replaced. Format: YYYYMMDD[-n].
export const OG_IMAGE_VERSION = "20260802";

/** Compact, URL-safe stamp derived from a date/ISO string. */
export function stampFrom(value) {
  if (!value) return "";
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return "";
  return String(Math.floor(time / 1000));
}

/**
 * Append the cache-busting version to an absolute image URL.
 * - leaves data: / blob: URLs untouched
 * - never double-stamps a URL that already carries `v=`
 * - preserves any existing query string
 */
export function versionedImageUrl(url, stamp) {
  const raw = String(url || "").trim();
  if (!raw) return raw;
  if (/^(data:|blob:)/i.test(raw)) return raw;
  if (/[?&]v=/.test(raw)) return raw;

  const version = stamp ? `${OG_IMAGE_VERSION}-${stamp}` : OG_IMAGE_VERSION;
  const [base, hash = ""] = raw.split("#");
  const separator = base.includes("?") ? "&" : "?";
  return `${base}${separator}v=${version}${hash ? `#${hash}` : ""}`;
}

export default versionedImageUrl;
